import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'

import { Image } from '../Texts'
import heroClientAgeas from '../../assets/images/hero/hero-ageas.png'
import heroClientEricsson from '../../assets/images/hero/hero-ericsson.png'
import heroClientNos from '../../assets/images/hero/hero-nos.png'
import heroClientPfizer from '../../assets/images/hero/hero-pfizer.png'
import heroClientWorten from '../../assets/images/hero/hero-worten.png'
import { getWorkShowcaseEntries } from '../../constants/workShowcase'

/** Visual order for the hero strip (showcase entries from `getWorkShowcaseEntries`). */
const WORK_HERO_SLIDER_IDS = [
  'gulbenkian-foundation',
  'jornal-economico',
  'sonae-sierra',
  'kicks',
  'trafiurbe',
  'unitel',
]

const SLIDER_GAP_PX = 26

const renderMarqueeItems = (projects, keySuffix, eagerFirst) =>
  projects.map((project, i) => (
    <div key={`${project.id}${keySuffix}`} className="work-hero__slide work-hero__marquee-item">
      <div className="work-hero__card-wrap">
        <div className="work-hero__card">
          <img
            src={project.image}
            alt=""
            className="work-hero__card-img"
            loading={eagerFirst && i === 0 ? 'eager' : 'lazy'}
            decoding="async"
          />
        </div>
      </div>
    </div>
  ))

const WorkOne = ({ lang }) => {
  const isEn = lang === 'en'
  const trackRef = useRef(null)
  const set1Ref = useRef(null)
  const set2Ref = useRef(null)
  const [marqueeReady, setMarqueeReady] = useState(false)

  const sliderProjects = useMemo(() => {
    const entries = getWorkShowcaseEntries(isEn)
    return WORK_HERO_SLIDER_IDS.map(id => entries.find(e => e.id === id)).filter(Boolean)
  }, [isEn])

  const syncMarqueeShift = () => {
    const track = trackRef.current
    const s2 = set2Ref.current
    if (!track || !s2) return
    const trackLeft = track.getBoundingClientRect().left
    const set2Left = s2.getBoundingClientRect().left
    const px = set2Left - trackLeft
    track.style.setProperty('--work-hero-marquee-shift', `${px}px`)
    setMarqueeReady(px > 1)
  }

  useLayoutEffect(() => {
    syncMarqueeShift()
    const track = trackRef.current
    const s1 = set1Ref.current
    const s2 = set2Ref.current
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncMarqueeShift, { passive: true })
      return () => window.removeEventListener('resize', syncMarqueeShift)
    }
    const ro = new ResizeObserver(() => {
      syncMarqueeShift()
    })
    if (track) ro.observe(track)
    if (s1) ro.observe(s1)
    if (s2) ro.observe(s2)
    window.addEventListener('resize', syncMarqueeShift, { passive: true })
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', syncMarqueeShift)
    }
  }, [sliderProjects])

  const clientLogos = [
    { image: heroClientEricsson, title: 'ERICSSON', width: 63, height: 55 },
    { image: heroClientAgeas, title: 'AGEAS', width: 102, height: 41 },
    { image: heroClientWorten, title: 'WORTEN', width: 102, height: 22 },
    { image: heroClientPfizer, title: 'PFIZER', width: 102, height: 42 },
    { image: heroClientNos, title: 'NOS', width: 85, height: 47 },
  ]

  const trustedLabel = isEn ? 'Trusted by' : 'Confiam em nós'
  const trustedLogosTwice = [...clientLogos, ...clientLogos]

  const subtext = isEn
    ? 'From big corporations to startups that are eager to change the world.'
    : 'De grandes empresas a startups que estão ansiosas por mudar o mundo.'

  return (
    <section id="work-one" className="work-hero">
      <div className="work-hero__inner content container">
        <h1 className="work-hero__title">
          {isEn ? (
            <>
              <span className="work-hero__title--muted">We Work with </span>
              <span className="work-hero__title--accent">Great Clients.</span>
            </>
          ) : (
            <>
              <span className="work-hero__title--muted">Trabalhamos com </span>
              <span className="work-hero__title--accent">Grandes Clientes.</span>
            </>
          )}
        </h1>
      </div>

      <div
        className="work-hero__slider-bleed"
        style={{ '--work-hero-marquee-gap': `${SLIDER_GAP_PX}px` }}
      >
        <div className="work-hero__marquee" role="presentation" aria-hidden="true">
          <div
            ref={trackRef}
            className={`work-hero__marquee-track${marqueeReady ? ' work-hero__marquee-track--ready' : ''}`}
          >
            <div ref={set1Ref} className="work-hero__marquee-set">
              {renderMarqueeItems(sliderProjects, '', true)}
            </div>
            <div ref={set2Ref} className="work-hero__marquee-set work-hero__marquee-set--clone" aria-hidden="true">
              {renderMarqueeItems(sliderProjects, '-dup', false)}
            </div>
          </div>
        </div>
      </div>

      <div className="work-hero__inner content container">
        <p className="work-hero__subtext">{subtext}</p>

        <div className="work-hero__trusted" role="region" aria-label={trustedLabel}>
          <p className="work-hero__trusted-label">{trustedLabel}</p>
          <ul className="work-hero__trusted-list">
            {trustedLogosTwice.map((c, idx) => (
              <li key={`${c.title}-${idx}`}>
                <Image {...c} loading="lazy" decoding="async" />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}

export default WorkOne
